import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Checkbox, Empty, Flex, Popconfirm, Space, Tag, Tooltip, Typography } from "antd";
import {
  CommentOutlined,
  DeleteOutlined,
  EditOutlined,
  ExportOutlined,
  MailOutlined,
  MessageOutlined,
  PhoneOutlined,
  PlusOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import { loadRecordMessages } from "api/communication";
import Attachment from "components/Communication/Attachment";
import { EmailBody, WhatsAppBody } from "components/Communication/EnvelopeMessage";
import HtmlEmailFrame, { looksLikeHtml } from "components/Communication/HtmlEmailFrame";
import { DeliveryStatus } from "components/Communication/MessageBubble";

const { Text } = Typography;

/**
 * The activity timeline for a lead or an account, rendered as a chat thread.
 *
 * Every activity is a bubble tagged with its channel; when the activity was a
 * template send, the delivered subject/body and its delivery status ride along
 * in the same bubble. Channel checkboxes at the top filter the thread.
 *
 * Given `record` ({ lead: id } or { account: id }), the thread also loads the
 * record's conversations from the Communication Box - what the customer wrote
 * back (left, like a chat) and what reps replied from the inbox (right) - and
 * merges them in by time. Replies are written in the inbox, one click away.
 */

export const CHANNELS = [
  { key: "WHATSAPP", label: "WhatsApp", icon: <WhatsAppOutlined />, color: "#25D366" },
  { key: "EMAIL", label: "Email", icon: <MailOutlined />, color: "#1677ff" },
  { key: "CALL", label: "Call", icon: <PhoneOutlined />, color: "#722ed1" },
  { key: "CHAT", label: "Website chat", icon: <CommentOutlined />, color: "#fa8c16" },
  { key: "OTHER", label: "Other", icon: <MessageOutlined />, color: "#8c8c8c" },
];

// Communication Box channel -> timeline channel.
const MESSAGE_CHANNEL = { WHATSAPP: "WHATSAPP", EMAIL: "EMAIL", CHAT: "CHAT" };
const messageChannel = (message) =>
  MESSAGE_CHANNEL[String(message.channel || "").toUpperCase()] || "OTHER";
const activityChannel = (activity) =>
  CHANNEL_KEYS.includes(activity.activity_type) ? activity.activity_type : "OTHER";

const CHANNEL_BY_KEY = CHANNELS.reduce((acc, c) => ({ ...acc, [c.key]: c }), {});
const CHANNEL_KEYS = CHANNELS.map((c) => c.key);
const ALL_KEYS = CHANNEL_KEYS;

// Strip tags for plain-text bodies (WhatsApp, or an email with no markup).
const asPlainText = (html) => {
  if (!html) return "";
  const el = document.createElement("div");
  el.innerHTML = html;
  return (el.textContent || el.innerText || "").replace(/\s+/g, " ").trim();
};

const ActivityBubble = ({ activity, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const channel = CHANNEL_BY_KEY[activity.activity_type] || CHANNEL_BY_KEY.OTHER;
  const message = activity.messages?.[0];
  const htmlEmail = message?.channel === "EMAIL" && looksLikeHtml(message.body);
  const body = message && !htmlEmail ? asPlainText(message.body) : "";
  const isLong = body.length > 260;

  return (
    <Flex justify="flex-end" style={{ marginBottom: 12 }}>
      <div
        style={{
          maxWidth: "78%",
          minWidth: 260,
          background: "#fff",
          border: "1px solid #f0f0f0",
          borderLeft: `3px solid ${channel.color}`,
          borderRadius: 8,
          padding: "10px 12px",
          boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        }}
      >
        <Flex justify="space-between" align="center" gap="small" style={{ marginBottom: 6 }}>
          <Space size={6}>
            <span style={{ color: channel.color }}>{channel.icon}</span>
            <Text strong style={{ fontSize: 12 }}>{channel.label}</Text>
            {activity.status && <Tag style={{ marginInlineEnd: 0 }}>{activity.status}</Tag>}
            {message?.delivery && (
              <DeliveryStatus
                status={message.delivery.status}
                receipts={message.delivery.receipts}
                errorMessage={message.delivery.error_message}
                sentAt={message.sent_at}
                trackDelivery={message.channel === "WHATSAPP"}
              />
            )}
            {/* Nothing went out from here - the rep sent it themselves and the
                CRM only recorded it. Worth saying, or the bubble reads as a
                delivery this system can vouch for. */}
            {message?.sent_externally && (
              <Tooltip title="Sent by hand from outside the system; recorded here only">
                <Tag color="gold" style={{ marginInlineEnd: 0 }}>BY HAND</Tag>
              </Tooltip>
            )}
          </Space>
          <Space size={10}>
            <Text type="secondary" style={{ fontSize: 11, whiteSpace: "nowrap" }}>
              {activity.activity_date
                ? dayjs(activity.activity_date).format("D MMM YYYY, h:mm a")
                : ""}
            </Text>
            {onEdit && (
              <a onClick={() => onEdit(activity)}>
                <EditOutlined style={{ fontSize: 12 }} />
              </a>
            )}
            {onDelete && (
              <Popconfirm title="Delete this activity?" onConfirm={() => onDelete(activity.id)}>
                <a>
                  <DeleteOutlined style={{ fontSize: 12 }} />
                </a>
              </Popconfirm>
            )}
          </Space>
        </Flex>

        {message?.subject && (
          <div style={{ marginBottom: 4 }}>
            <Text strong style={{ fontSize: 13 }}>{message.subject}</Text>
          </div>
        )}

        {htmlEmail && <HtmlEmailFrame html={message.body} previewHeight={240} />}

        {body && (
          <div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>
            {expanded || !isLong ? body : `${body.slice(0, 260)}…`}
            {isLong && (
              <a style={{ marginLeft: 6 }} onClick={() => setExpanded((v) => !v)}>
                {expanded ? "show less" : "show more"}
              </a>
            )}
          </div>
        )}

        {activity.notes && (
          <div style={{ fontSize: 12, color: "#666", marginTop: body || htmlEmail ? 6 : 0 }}>
            {activity.notes}
          </div>
        )}

        {message?.delivery?.status === "failed" && message.delivery.error_message && (
          <div style={{ fontSize: 12, color: "#ff4d4f", marginTop: 6 }}>
            Not delivered: {message.delivery.error_message}
          </div>
        )}

        {message?.to_address && (
          <div style={{ fontSize: 11, color: "#999", marginTop: 6 }}>
            to {message.to_address}
            {message.template_title ? ` · ${message.template_title}` : ""}
          </div>
        )}
      </div>
    </Flex>
  );
};

/** A Communication Box message: the customer's (left) or an inbox reply (right). */
const ConversationBubble = ({ message }) => {
  const inbound = message.direction === "inbound";
  const channel = CHANNEL_BY_KEY[messageChannel(message)];
  const who = inbound
    ? message.meta?.from_name || message.sender_name || "Customer"
    : message.sender_name || message.meta?.sender_name || "Sent from inbox";
  const inboxLink = message.conversation_ref
    ? `/communication?${new URLSearchParams({
        channel: message.channel,
        c: message.conversation_ref,
      })}`
    : null;

  let body;
  if (channel.key === "EMAIL") {
    body = (
      <>
        {message.subject && (
          <div style={{ marginBottom: 4 }}>
            <Text strong style={{ fontSize: 13 }}>{message.subject}</Text>
          </div>
        )}
        <EmailBody message={message} />
      </>
    );
  } else if (channel.key === "WHATSAPP") {
    body = <WhatsAppBody message={message} />;
  } else {
    body = (
      <>
        {(message.attachments || []).map((attachment) => (
          <div key={attachment.id} style={{ marginBottom: 4 }}>
            <Attachment attachment={attachment} />
          </div>
        ))}
        {message.body && (
          <div style={{ fontSize: 13, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {message.body}
          </div>
        )}
      </>
    );
  }

  return (
    <Flex justify={inbound ? "flex-start" : "flex-end"} style={{ marginBottom: 12 }}>
      <div
        style={{
          maxWidth: "78%",
          minWidth: 260,
          background: inbound ? "#fff" : "#f0f9ff",
          border: "1px solid #f0f0f0",
          [inbound ? "borderRight" : "borderLeft"]: `3px solid ${channel.color}`,
          borderRadius: 8,
          padding: "10px 12px",
          boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        }}
      >
        <Flex justify="space-between" align="center" gap="small" style={{ marginBottom: 6 }}>
          <Space size={6}>
            <span style={{ color: channel.color }}>{channel.icon}</span>
            <Text strong style={{ fontSize: 12 }}>{who}</Text>
            {inbound ? (
              <Tag color="cyan" style={{ marginInlineEnd: 0 }}>REPLY</Tag>
            ) : (
              <Tag style={{ marginInlineEnd: 0 }}>FROM INBOX</Tag>
            )}
          </Space>
          <Space size={10}>
            <Text type="secondary" style={{ fontSize: 11, whiteSpace: "nowrap" }}>
              {message.sent_at ? dayjs(message.sent_at).format("D MMM YYYY, h:mm a") : ""}
            </Text>
            {!inbound && (channel.key === "WHATSAPP" || channel.key === "EMAIL") && (
              <DeliveryStatus
                status={message.status}
                receipts={message.meta?.receipts}
                errorMessage={message.error_message}
                sentAt={message.sent_at}
                trackDelivery={channel.key === "WHATSAPP"}
              />
            )}
            {inboxLink && (
              <Tooltip title={inbound ? "Reply in the Communication Box" : "Open in the Communication Box"}>
                <Link to={inboxLink} aria-label="Open in inbox">
                  <ExportOutlined style={{ fontSize: 12 }} />
                </Link>
              </Tooltip>
            )}
          </Space>
        </Flex>
        {body}
      </div>
    </Flex>
  );
};

/**
 * The record's Communication Box messages, newest first, with paging back.
 * Reloads when `refreshKey` changes and when the window regains focus - a
 * reply that arrived while the rep was elsewhere shows up on return.
 */
const useRecordMessages = (record, refreshKey) => {
  const [messages, setMessages] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const recordKey = record ? JSON.stringify(record) : null;

  const reload = useCallback(async () => {
    if (!recordKey) return;
    try {
      const response = await loadRecordMessages(JSON.parse(recordKey));
      setMessages(response.data?.results || []);
      setNextBefore(response.data?.next_before || null);
    } catch (err) {
      // The activities still show; conversations are an addition to them.
      setMessages([]);
      setNextBefore(null);
    }
  }, [recordKey]);

  useEffect(() => {
    reload();
  }, [reload, refreshKey]);

  useEffect(() => {
    if (!recordKey) return undefined;
    window.addEventListener("focus", reload);
    return () => window.removeEventListener("focus", reload);
  }, [reload, recordKey]);

  const loadOlder = async () => {
    if (!nextBefore) return;
    setLoadingOlder(true);
    try {
      const response = await loadRecordMessages({ ...JSON.parse(recordKey), before: nextBefore });
      setMessages((current) => {
        const seen = new Set(current.map((message) => `${message.channel}:${message.id}`));
        return [
          ...current,
          ...(response.data?.results || []).filter(
            (message) => !seen.has(`${message.channel}:${message.id}`)
          ),
        ];
      });
      setNextBefore(response.data?.next_before || null);
    } catch (err) {
      setNextBefore(null);
    }
    setLoadingOlder(false);
  };

  return { messages, nextBefore, loadOlder, loadingOlder };
};

const ActivityThread = ({
  activities = [],
  onAdd,
  onEdit,
  onDelete,
  addLabel = "Add Activity",
  // { lead: id } or { account: id }: also show the record's conversations.
  record,
  // Fills the pane it sits in. The offset covers the page header, the tab
  // strip and the channel filters above the thread.
  height = "calc(100vh - 330px)",
}) => {
  const [visible, setVisible] = useState(ALL_KEYS);
  const scrollRef = useRef(null);
  // New activities (a send, an edit) are a reason to look for replies too.
  const { messages, nextBefore, loadOlder, loadingOlder } = useRecordMessages(record, activities);

  // One list of both kinds, each tagged with its channel and time.
  const items = useMemo(
    () => [
      ...activities.map((activity) => ({
        kind: "activity",
        key: `a:${activity.id}`,
        channel: activityChannel(activity),
        at: activity.activity_date || activity.created_at,
        created: activity.created_at,
        activity,
      })),
      ...messages.map((message) => ({
        kind: "message",
        key: `m:${message.channel}:${message.id}`,
        channel: messageChannel(message),
        at: message.sent_at,
        created: message.sent_at,
        message,
      })),
    ],
    [activities, messages]
  );

  const counts = useMemo(() => {
    const tally = {};
    items.forEach((item) => {
      tally[item.channel] = (tally[item.channel] || 0) + 1;
    });
    return tally;
  }, [items]);

  // Newest first. Two activities logged for the same minute fall back to
  // when they were created, so the one just sent still lands on top.
  const shown = useMemo(() => {
    const time = (value) => (value ? dayjs(value).valueOf() : 0);
    return [...items]
      .sort((a, b) => time(b.at) - time(a.at) || time(b.created) - time(a.created))
      .filter((item) => visible.includes(item.channel));
  }, [items, visible]);

  // Something new appears at the top, so bring the view back up to it.
  const newest = shown[0]?.key;
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = 0;
  }, [newest]);

  return (
    <div>
      <Flex justify="space-between" align="center" wrap="wrap" gap="small" style={{ marginBottom: 12 }}>
        <Checkbox.Group value={visible} onChange={setVisible}>
          {CHANNELS.map((channel) => (
            <Checkbox key={channel.key} value={channel.key}>
              <Space size={4}>
                <span style={{ color: channel.color }}>{channel.icon}</span>
                {channel.label}
                {counts[channel.key] ? (
                  <Text type="secondary">({counts[channel.key]})</Text>
                ) : null}
              </Space>
            </Checkbox>
          ))}
        </Checkbox.Group>
        {onAdd && (
          <Button type="primary" icon={<PlusOutlined />} onClick={onAdd}>
            {addLabel}
          </Button>
        )}
      </Flex>

      <div
        ref={scrollRef}
        className="custom-scroll-div"
        style={{
          background: "#f5f5f5",
          borderRadius: 8,
          padding: 12,
          height,
          minHeight: 280,
          overflowY: "auto",
        }}
      >
        {shown.length === 0 ? (
          <Flex align="center" justify="center" style={{ height: "100%" }}>
            <Empty
              description={
                items.length
                  ? "No activities match the selected channels"
                  : "No activities yet"
              }
            />
          </Flex>
        ) : (
          <>
            {shown.map((item) =>
              item.kind === "message" ? (
                <ConversationBubble key={item.key} message={item.message} />
              ) : (
                <ActivityBubble
                  key={item.key}
                  activity={item.activity}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              )
            )}
            {nextBefore && (
              <Flex justify="center">
                <Button size="small" loading={loadingOlder} onClick={loadOlder}>
                  Load older conversations
                </Button>
              </Flex>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ActivityThread;
